<template>
	<div
		class="m-stickyCar"
		:class="containerModifierClass"
		v-if="store.isReady && !isPriceTooLow"
	>
		<div class="m-stickyCar__image">
			<img
				v-if="carImageUrl"
				:src="carImageUrl"
				:alt="carImageAlt"
			>
		</div>

		<div class="m-stickyCar__content">
			<div class="m-stickyCar__left">
				<span class="m-stickyCar__title u-fs28 u-fw550">
					{{ store.carTitle || fallbackTitle }}
				</span>
				<div class="m-stickyCar__tags" v-if="store.carTags && store.carTags.length > 0">
					<span 
						v-for="(tag, index) in store.carTags" 
						:key="index" 
						class="a-tag u-colorGray u-bgBlue"
					>
						{{ tag }}
					</span>
				</div>
			</div>

			<div class="m-stickyCar__right">

				<div v-if="!isPriceTooLow" class="m-stickyCar__column">
					<div>
						<div
							class="u-priceUpdating m-stickyCar__price"
							:class="{ '-isUpdating': store.isPriceUpdating }"
						>
							<template v-if="store.isPriceUpdating">
								<span class="u-colorGray"><Loader size="small" /></span>
							</template>
							<template v-else-if="hasLeaseValues">
								<strong>{{ store.formattedMaandbedrag }}</strong>
								<span class="u-colorGray"> p/m</span>
							</template>
							<template v-else>
								<span class="u-colorGray">N.t.b.</span>
							</template>
							<button
								v-if="!store.isPriceUpdating"
								type="button"
								class="a-editPriceButton"
								aria-label="Wijzig aanbetaling en slottermijn"
								title="Wijzig aanbetaling en slottermijn"
								@click="handleEditPriceClick"
							>
								<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M8.26952 0.496817L7.2173 1.54904L10.449 4.7807L11.5012 3.72847C11.8199 3.4121 11.998 2.9809 11.998 2.53096C11.998 2.08101 11.8199 1.64981 11.5012 1.33344L10.6646 0.496817C10.3482 0.178104 9.91698 0 9.46704 0C9.01709 0 8.58589 0.178104 8.26952 0.496817ZM6.42286 2.34348L1.37969 7.3843C1.12894 7.63505 0.94615 7.94908 0.850068 8.29123L0.0204764 11.2862C-0.0334236 11.4807 0.0204764 11.6916 0.165772 11.8346C0.311068 11.9775 0.519637 12.0338 0.714146 11.9799L3.70911 11.1479C4.05126 11.0518 4.36294 10.8714 4.61604 10.6183L9.65452 5.57513L6.42286 2.34348Z" fill="#6E6E6E"/>
								</svg>
							</button>
						</div>
						<span v-if="store.looptijd" class="u-fs14 u-colorGray">o.b.v. {{ store.looptijd }} mnd</span>
					</div>
					<button
						type="button"
						:class="leaseButtonClasses"
						class="-fullWidth"
						:disabled="leaseButtonDisabled"
						:aria-disabled="leaseButtonDisabled ? 'true' : 'false'"
						@click="handleLeaseClick"
					>
						{{ leaseButtonLabel }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, watch, onMounted, onBeforeUnmount, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useFormStore } from '../stores/form.js';
import Loader from './Loader.vue';

const props = defineProps( {
	stickyVisible: {
		type: Boolean,
		default: false,
	},
} );

const store = useFormStore();
const router = useRouter();
const copyConfig = inject( 'copyConfig', {} );

const fallbackTitle = 'Auto';

/**
 * Haalt een sticky-knoplabel op. De sticky-buttons zijn los te beheren van
 * de gewone buttons: alleen de sticky-specifieke key wordt gebruikt, met een
 * directe terugval op de hardcoded default (dezelfde default als voorheen).
 *
 * @param {string} stickyKey Sticky-specifieke copyConfig key (api_*_sticky_*_button).
 * @param {string} fallback  Hardcoded default.
 * @return {string} Het te tonen label.
 */
const resolveButtonLabel = ( stickyKey, fallback ) => {
	if ( ! copyConfig || typeof copyConfig !== 'object' ) {
		return fallback;
	}
	return copyConfig[stickyKey] || fallback;
};

const carImageUrl = computed( () => {
	return store.carImageUrl || '';
} );

const carImageAlt = computed( () => {
	if ( store.carTitle ) {
		return `Afbeelding van ${ store.carTitle }`;
	}
	return 'Afbeelding van auto';
} );

const hasLeaseValues = computed( () => {
	return store.aanbetaling !== null && store.slottermijn !== null;
} );

const containerModifierClass = computed( () => '-isZakelijk' );

const leaseButtonDisabled = computed( () => {
	return ! store.isReady || ! store.isStep1Valid;
} );

const leaseButtonLabel = computed( () =>
	resolveButtonLabel( 'api_zakelijk_sticky_lease_button', 'Financial lease' )
);

const leaseButtonClasses = computed( () => 'a-button -primary' );

const isPriceTooLow = computed( () => {
	// Verberg de sticky-bar alleen als er geen lease-data beschikbaar is.
	return ! store.hasLeaseData;
} );

const proceedToStep2 = () => {
	store.markStepCompleted( 1 );
	router.push( '/step/2' );
};

const handleEditPriceClick = () => {
	document.dispatchEvent(
		new CustomEvent(
			'greven:editPriceClick',
			{
				bubbles: false,
			}
		)
	);
};

const handleLeaseClick = () => {
	store.setSubmissionMode( 'lease' );

	if ( ! store.isStep1Valid ) {
		// Let main step 1 view handle focusing the first error
		document.dispatchEvent(
			new CustomEvent(
				'greven:stickyLeaseClickInvalid',
				{
					bubbles: false,
				}
			)
		);
		return;
	}

	proceedToStep2();
};

const updateStickyWrapperVisibility = ( visible ) => {
	const wrapper = document.querySelector( '.o-carSingle__stickyCar' );

	if ( ! wrapper ) {
		return;
	}

	if ( visible ) {
		wrapper.classList.add( '-visible' );
	} else {
		wrapper.classList.remove( '-visible' );
	}
};

onMounted( () => {
	updateStickyWrapperVisibility( props.stickyVisible );
} );

watch(
	() => props.stickyVisible,
	( value ) => {
		updateStickyWrapperVisibility( value );
	}
);

onBeforeUnmount( () => {
	updateStickyWrapperVisibility( false );
} );
</script>


